import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useDiet } from "@/contexts/DietContext";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { motion } from "framer-motion";
import { Search, Plus, Trash2, Calculator, ChefHat, Flame, Beef, Droplets, Wheat, BookOpen, Globe, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSavedRecipes } from "@/hooks/useSavedRecipes";
import { useAuth } from "@/contexts/AuthContext";
import SavedRecipesList from "@/components/recipes/SavedRecipesList";
import RecipeIconPicker from "@/components/recipes/RecipeIconPicker";

interface TacoFood {
  id: number;
  taco_id: number;
  description: string;
  category: string;
  energy_kcal: number;
  protein_g: number;
  lipid_g: number;
  carbohydrate_g: number;
  fiber_g: number;
  source: string;
}

interface RecipeIngredient {
  food: TacoFood;
  quantity_grams: number;
}

const RecipeCalculator = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { selectedPlan } = useDiet();
  const navigate = useNavigate();
  const { recipes: savedRecipes, globalRecipes, loading: recipesLoading, fetchRecipes, deleteRecipe, updateVisibility, updateRecipe } = useSavedRecipes();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<TacoFood[]>([]);
  const [loading, setLoading] = useState(false);
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [recipeName, setRecipeName] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Edit mode
  const [editingRecipeId, setEditingRecipeId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Admin-only fields
  const [isGlobal, setIsGlobal] = useState(false);
  const [visBasic, setVisBasic] = useState(false);
  const [visPlus, setVisPlus] = useState(false);
  const [visComplete, setVisComplete] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  // Generate preview URL for banner
  useEffect(() => {
    if (!bannerFile) { setBannerPreview(null); return; }
    const url = URL.createObjectURL(bannerFile);
    setBannerPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [bannerFile]);

  // Check admin role
  useEffect(() => {
    if (!user) return;
    const check = async () => {
      try {
        const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
        setIsAdmin(data === true);
      } catch { setIsAdmin(false); }
    };
    check();
  }, [user]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = useCallback((query: string) => {
    setSearch(query);
    if (query.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await apiFetch("api-foods", { q: query });
        setResults((res.data as TacoFood[]) || []);
      } catch (e: any) {
        console.error("Search error:", e);
        setResults([]);
      }
      setLoading(false);
    }, 350);
  }, []);

  const addIngredient = (food: TacoFood) => {
    if (ingredients.find(i => i.food.id === food.id)) {
      toast({ title: "Alimento já adicionado", variant: "destructive" });
      return;
    }
    setIngredients(prev => [...prev, { food, quantity_grams: 100 }]);
    setSearch("");
    setResults([]);
  };

  const removeIngredient = (foodId: number) => {
    setIngredients(prev => prev.filter(i => i.food.id !== foodId));
  };

  const updateQuantity = (foodId: number, grams: number) => {
    setIngredients(prev =>
      prev.map(i => i.food.id === foodId ? { ...i, quantity_grams: Math.max(1, grams) } : i)
    );
  };

  const totals = useMemo(() => {
    return ingredients.reduce(
      (acc, ing) => {
        const factor = ing.quantity_grams / 100;
        return {
          kcal: acc.kcal + ing.food.energy_kcal * factor,
          protein: acc.protein + ing.food.protein_g * factor,
          lipid: acc.lipid + ing.food.lipid_g * factor,
          carb: acc.carb + ing.food.carbohydrate_g * factor,
          fiber: acc.fiber + ing.food.fiber_g * factor,
          grams: acc.grams + ing.quantity_grams,
        };
      },
      { kcal: 0, protein: 0, lipid: 0, carb: 0, fiber: 0, grams: 0 }
    );
  }, [ingredients]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data: tacoData, error: tacoError } = await supabase.functions.invoke("seed-taco");
      if (tacoError) throw tacoError;
      const { data: tbcaData, error: tbcaError } = await supabase.functions.invoke("seed-tbca");
      if (tbcaError) throw tbcaError;
      toast({ title: "Dados carregados! 🎉", description: `TACO: ${tacoData.message} | TBCA: ${tbcaData.message}` });
    } catch (e: any) {
      toast({ title: "Erro ao carregar dados", description: e.message, variant: "destructive" });
    }
    setSeeding(false);
  };

  const [seedingUsda, setSeedingUsda] = useState(false);

  const handleSeedUsda = async () => {
    setSeedingUsda(true);
    try {
      const { data, error } = await supabase.functions.invoke("seed-usda");
      if (error) throw error;
      toast({ title: "USDA carregada! 🇺🇸", description: data.message });
    } catch (e: any) {
      toast({ title: "Erro ao carregar USDA", description: e.message, variant: "destructive" });
    }
    setSeedingUsda(false);
  };

  const handleEditRecipe = useCallback((recipe: import("@/hooks/useSavedRecipes").SavedRecipe) => {
    setEditingRecipeId(recipe.id);
    setRecipeName(recipe.name);
    setIsGlobal(recipe.is_global);
    setVisBasic(recipe.visible_basic);
    setVisPlus(recipe.visible_plus);
    setVisComplete(recipe.visible_complete);
    setSelectedIcon(recipe.icon || null);
    setBannerFile(null);
    setBannerPreview(recipe.banner_image_url || null);
    setIngredients(
      recipe.recipe_ingredients.map(ing => ({
        food: {
          id: ing.taco_foods.id,
          taco_id: 0,
          description: ing.taco_foods.description,
          category: ing.taco_foods.category,
          energy_kcal: ing.taco_foods.energy_kcal || 0,
          protein_g: ing.taco_foods.protein_g || 0,
          lipid_g: ing.taco_foods.lipid_g || 0,
          carbohydrate_g: ing.taco_foods.carbohydrate_g || 0,
          fiber_g: ing.taco_foods.fiber_g || 0,
          source: "",
        },
        quantity_grams: ing.quantity_grams,
      }))
    );
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const clearForm = () => {
    setRecipeName("");
    setIngredients([]);
    setIsGlobal(false);
    setVisBasic(false);
    setVisPlus(false);
    setVisComplete(false);
    setSelectedIcon(null);
    setBannerFile(null);
    setEditingRecipeId(null);
  };

  const handleSaveRecipe = async () => {
    if (!recipeName.trim()) {
      toast({ title: "Digite o nome da receita", variant: "destructive" });
      return;
    }
    if (ingredients.length === 0) {
      toast({ title: "Adicione pelo menos um ingrediente", variant: "destructive" });
      return;
    }
    setIsSaving(true);
    try {
      const isGlobalRecipe = isAdmin && isGlobal;
      const ingredientData = ingredients.map(i => ({
        taco_food_id: i.food.id,
        quantity_grams: i.quantity_grams,
      }));

      if (editingRecipeId) {
        await updateRecipe(editingRecipeId, {
          name: recipeName,
          ingredients: ingredientData,
          is_global: isGlobalRecipe,
          visible_basic: isGlobalRecipe ? visBasic : false,
          visible_plus: isGlobalRecipe ? visPlus : false,
          visible_complete: isGlobalRecipe ? visComplete : false,
          icon: isGlobalRecipe ? selectedIcon : null,
          bannerFile: bannerFile,
        });
        toast({ title: "Receita atualizada com sucesso! ✏️" });
      } else if (bannerFile && isGlobalRecipe) {
        const { compressImage } = await import("@/lib/imageCompressor");
        const compressedBanner = await compressImage(bannerFile);
        const formData = new FormData();
        formData.append("name", recipeName);
        formData.append("ingredients", JSON.stringify(ingredientData));
        formData.append("is_global", "true");
        formData.append("visible_basic", visBasic ? "true" : "false");
        formData.append("visible_plus", visPlus ? "true" : "false");
        formData.append("visible_complete", visComplete ? "true" : "false");
        if (selectedIcon) formData.append("icon", selectedIcon);
        formData.append("banner_image", compressedBanner);

        await apiFetch("api-recipes", { action: "create" }, {
          method: "POST",
          formData,
        });
        toast({ title: "Receita salva com sucesso! 🎉" });
      } else {
        await apiFetch("api-recipes", { action: "create" }, {
          method: "POST",
          body: {
            name: recipeName,
            ingredients: ingredientData,
            is_global: isGlobalRecipe,
            visible_basic: isGlobalRecipe ? visBasic : false,
            visible_plus: isGlobalRecipe ? visPlus : false,
            visible_complete: isGlobalRecipe ? visComplete : false,
            icon: isGlobalRecipe ? selectedIcon : null,
          },
        });
        toast({ title: "Receita salva com sucesso! 🎉" });
      }

      clearForm();
      await fetchRecipes();
    } catch (e: any) {
      toast({ title: "Erro ao salvar receita", description: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const hasPlan = selectedPlan && ["plus", "complete"].includes(selectedPlan);

  if (!hasPlan) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-20 text-center">
        <Lock className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-xl font-bold text-foreground mb-2">Calculadora de Calorias</h2>
        <p className="text-muted-foreground mb-6 max-w-sm">
          Este recurso está disponível a partir do plano PRO FIT PLUS. Faça upgrade para acessar.
        </p>
        <Button onClick={() => navigate("/plans")} className="gradient-primary text-primary-foreground">
          Ver Planos
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="text-center sm:text-left">
        <h1 className="text-2xl font-bold text-foreground">Calculadora de Calorias</h1>
        <p className="text-muted-foreground">Busque alimentos e monte suas receitas</p>
      </div>

      {/* Search */}
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Search className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Buscar Alimento</h2>
        </div>

        <div className="relative">
          <Input
            placeholder="Digite o nome do alimento (ex: banana, arroz, frango)..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
            className="pr-10"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {results.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-1 max-h-64 overflow-y-auto"
          >
            {results.map(food => (
              <button
                key={food.id}
                onClick={() => addIngredient(food)}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-muted transition-colors text-left"
              >
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-foreground">{food.description}</p>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide shrink-0">{food.source}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{food.category}</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground whitespace-nowrap">
                  <span className="text-primary font-medium">{food.energy_kcal.toFixed(0)} kcal</span>
                  <span>{food.protein_g.toFixed(1)}g P</span>
                  <span>{food.carbohydrate_g.toFixed(1)}g C</span>
                  <span>{food.lipid_g.toFixed(1)}g G</span>
                  <Plus className="w-4 h-4 text-primary" />
                </div>
              </button>
            ))}
          </motion.div>
        )}

        {search.length >= 2 && results.length === 0 && !loading && (
          <div className="text-center py-4 space-y-3">
            <p className="text-sm text-muted-foreground">Nenhum alimento encontrado.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding}>
                {seeding ? "Carregando TACO + TBCA..." : "Carregar TACO + TBCA (~6.000)"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleSeedUsda} disabled={seedingUsda}>
                {seedingUsda ? "Carregando USDA..." : "Carregar USDA (~7.800 🇺🇸)"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Ingredients List */}
      {ingredients.length > 0 && (
        <div className="bg-card rounded-2xl p-5 shadow-card border border-border space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Ingredientes da Receita</h2>
          </div>

          <div className="space-y-3">
            {ingredients.map((ing, idx) => {
              const factor = ing.quantity_grams / 100;
              return (
                <motion.div
                  key={ing.food.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border bg-background"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{ing.food.description}</p>
                    <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                      <span className="text-primary font-medium">{(ing.food.energy_kcal * factor).toFixed(0)} kcal</span>
                      <span>{(ing.food.protein_g * factor).toFixed(1)}g P</span>
                      <span>{(ing.food.carbohydrate_g * factor).toFixed(1)}g C</span>
                      <span>{(ing.food.lipid_g * factor).toFixed(1)}g G</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={ing.quantity_grams}
                      onChange={e => updateQuantity(ing.food.id, parseFloat(e.target.value) || 0)}
                      className="w-20 text-center text-sm"
                      min={1}
                    />
                    <span className="text-xs text-muted-foreground">g</span>
                    <button
                      onClick={() => removeIngredient(ing.food.id)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Totals */}
          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Calculator className="w-4 h-4 text-primary" />
              Nutrientes Totais ({totals.grams.toFixed(0)}g)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <NutrientCard icon={Flame} label="Calorias" value={`${totals.kcal.toFixed(0)}`} unit="kcal" color="text-primary" />
              <NutrientCard icon={Beef} label="Proteínas" value={`${totals.protein.toFixed(1)}`} unit="g" color="text-primary" />
              <NutrientCard icon={Wheat} label="Carboidratos" value={`${totals.carb.toFixed(1)}`} unit="g" color="text-accent" />
              <NutrientCard icon={Droplets} label="Gorduras" value={`${totals.lipid.toFixed(1)}`} unit="g" color="text-warning" />
              <NutrientCard icon={Wheat} label="Fibras" value={`${totals.fiber.toFixed(1)}`} unit="g" color="text-muted-foreground" />
            </div>
          </div>

          {/* Save */}
          <div className="border-t border-border pt-4 space-y-3">
            <Input
              placeholder="Nome da receita (ex: Café da manhã fit)"
              value={recipeName}
              onChange={e => setRecipeName(e.target.value)}
            />

            {/* Admin: global toggle and plan visibility */}
            {isAdmin && (
              <div className="space-y-3 p-3 rounded-xl border border-primary/20 bg-primary/5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Globe className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground flex-1">Receita global (disponível para usuários)</span>
                  <Switch checked={isGlobal} onCheckedChange={setIsGlobal} />
                </label>

                {isGlobal && (
                  <div className="pl-6 space-y-3">
                    <p className="text-xs text-muted-foreground">Visível para:</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={visBasic} onCheckedChange={setVisBasic} />
                      <span className="text-xs text-foreground">PRO FIT</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={visPlus} onCheckedChange={setVisPlus} />
                      <span className="text-xs text-foreground">PRO FIT PLUS</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={visComplete} onCheckedChange={setVisComplete} />
                      <span className="text-xs text-foreground">PRO FIT MAX</span>
                    </label>

                    <div className="border-t border-primary/20 pt-3">
                      <RecipeIconPicker
                        selectedIcon={selectedIcon}
                        onSelectIcon={setSelectedIcon}
                        bannerFile={bannerFile}
                        onBannerFileChange={setBannerFile}
                        bannerPreview={bannerPreview}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              {editingRecipeId && (
                <Button
                  onClick={clearForm}
                  variant="outline"
                  size="lg"
                  className="flex-1"
                >
                  Cancelar
                </Button>
              )}
              <Button
                onClick={handleSaveRecipe}
                disabled={isSaving}
                className="flex-1 gradient-primary text-primary-foreground hover:opacity-90"
                size="lg"
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    {editingRecipeId ? "Atualizando..." : "Salvando..."}
                  </span>
                ) : (
                  editingRecipeId ? "Atualizar Receita" : "Salvar Receita"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Recipes */}
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Receitas Salvas</h2>
        </div>

        <SavedRecipesList
          recipes={savedRecipes}
          globalRecipes={globalRecipes}
          isAdmin={isAdmin}
          onDelete={deleteRecipe}
          onUpdateVisibility={isAdmin ? updateVisibility : undefined}
          onEdit={handleEditRecipe}
          loading={recipesLoading}
        />
      </div>
    </motion.div>
  );
};

const NutrientCard = ({ icon: Icon, label, value, unit, color }: {
  icon: any; label: string; value: string; unit: string; color: string;
}) => (
  <div className="bg-muted/50 rounded-xl p-3 text-center">
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <p className="text-lg font-bold text-foreground">{value}<span className="text-xs font-normal text-muted-foreground ml-0.5">{unit}</span></p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

export default RecipeCalculator;
